import { formatParisDateTime, type DashboardHealth } from '@flux/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { JSX } from 'react';
import { PageSection } from '@/components/layout/page.js';
import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { queryKeys } from '@/lib/query/keys.js';
import { toast } from '@/lib/toast.js';
import { refreshDashboardHealth } from './api.js';

const STATUS = {
  ok: { label: 'Sain', tone: 'green' },
  degraded: { label: 'Indisponible', tone: 'red' },
  unknown: { label: 'Inconnu', tone: 'gray' },
  inactive: { label: 'Inactif', tone: 'gray' },
} as const;

function displayTime(value: string | null): string {
  return value ? formatParisDateTime(new Date(value)) : 'Aucune activité connue';
}

/** Résultats de sondes en arrière-plan ; la disponibilité du worker entre dans le bilan global. */
export function HealthRow({ health }: { health: DashboardHealth }): JSX.Element {
  const queryClient = useQueryClient();
  const refresh = useMutation({
    mutationFn: refreshDashboardHealth,
    onSuccess: () => {
      toast.success('Contrôle des connexions lancé.');
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
    onError: (error: unknown) => toast.fromError(error),
  });
  const overall = health.overall ?? 'unknown';
  const integrations = health.integrations ?? [];
  return (
    <PageSection title="Santé des services" actions={
      <Button type="button" variant="outline" size="sm" isLoading={refresh.isPending}
        onClick={() => refresh.mutate()}>Vérifier maintenant</Button>
    }>
      <div className="flex items-center gap-2 border-b border-border pb-4">
        <Badge tone={STATUS[overall].tone}>{STATUS[overall].label}</Badge>
        {overall === 'unknown' ? <span className="text-xs text-muted-foreground">En attente du premier contrôle</span> : null}
      </div>
      <ul className="divide-y divide-border">
        {integrations.map((item) => (
          <li key={item.key} className="flex flex-wrap items-start justify-between gap-2 py-3">
            <div className="min-w-0">
              <p className="text-sm font-medium">{item.label}</p>
              <p className="text-xs text-muted-foreground">Dernière activité : {displayTime(item.lastActivityAt)}</p>
              <p className="text-xs text-muted-foreground">Dernier contrôle : {item.checkedAt ? displayTime(item.checkedAt) : 'Jamais'}</p>
              {item.status === 'degraded' && item.message ? <p className="text-xs text-status-red-fg">{item.message}</p> : null}
            </div>
            <Badge tone={STATUS[item.status].tone}>{STATUS[item.status].label}</Badge>
          </li>
        ))}
      </ul>
    </PageSection>
  );
}
